import { useAtomValue } from 'jotai';
import type { ComponentProps } from 'react';
import { Link as RouterLink } from 'react-router';

import { CASE_ID_PATTERN_SOURCE } from '@/constants/caseId.constants';
import { INTERNAL_DOMAIN_REGEX } from '@/constants/common';
import { LOGSCALE_URL_REGEX } from '@/pages/Ticket';
import { userIsInternalAtom } from '@/store/user.store';
import { convertLogscaleLinks, extractCaseId } from '@/utils/Common.utils';

import PonduranceEmailButton from '../PonduranceEmailButton';
import PonduranceLinkButton from '../PonduranceLinkButton';

const CASE_PATH_REGEX = new RegExp(`^/case/${CASE_ID_PATTERN_SOURCE}/?$`, 'i');

const MarkdownLink = (props: ComponentProps<'a'>) => {
  const { children, href } = props;
  const userIsInternal = useAtomValue(userIsInternalAtom);
  const rawHref = typeof href === 'string' ? href.trim() : '';

  const childText = typeof children === 'string' ? children : '';
  const targetUrl = rawHref || childText;

  const isDomainMatch = INTERNAL_DOMAIN_REGEX.test(targetUrl);

  if (isDomainMatch) {
    // A case link is identified by its path shape, not by substring-matching
    // the whole URL - a substring check can misclassify any pondurance.com
    // URL that happens to contain the same characters elsewhere in its path.
    let pathname = '';
    try {
      pathname = new URL(targetUrl).pathname;
    } catch {
      // targetUrl already matched INTERNAL_DOMAIN_REGEX (which requires a scheme), so this shouldn't happen in practice.
    }

    const isCaseUrl = CASE_PATH_REGEX.test(pathname);
    const isUrl = !targetUrl.includes('@pondurance.com') && !isCaseUrl;

    if (!targetUrl) {
      return null;
    }

    // is pondurance domain
    if (isUrl) {
      // is pondurance logscale domain (conversion to sso url)
      if (targetUrl.match(LOGSCALE_URL_REGEX)) {
        return <PonduranceLinkButton url={convertLogscaleLinks(targetUrl)} />;
      }
      return <PonduranceLinkButton url={targetUrl} />;
    }

    // Handle Markdown link usage
    if (isCaseUrl) {
      const caseId = extractCaseId(targetUrl);
      if (!caseId) {
        return <span>{children}</span>;
      }
      const caseLink = userIsInternal
        ? `/case-internal/${caseId}`
        : `/case/${caseId}`;
      return <RouterLink to={caseLink}>{caseId}</RouterLink>;
    }

    // is pondurance email
    return <PonduranceEmailButton email={targetUrl} />;
  }
  // Allow Pondurance email autolinks, but block all other non-Pondurance links from being clickable.
  if (/^[^\s@]+@pondurance\.com$/i.test(childText)) {
    return <PonduranceEmailButton email={childText} />;
  }

  // All non-pondurance links should not be clickable
  return <>{children}</>;
};

export default MarkdownLink;
